import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';
import Logo from '../components/Logo';
import { ArrowLeft, LogOut, Save, Languages } from 'lucide-react';

import { useMenuDraft } from '../hooks/useMenuDraft';
import { useTranslate, useBatchTranslate } from '../hooks/useTranslate';
import { ItemsEditor } from '../components/admin/ItemsEditor';
import { CategoriesEditor } from '../components/admin/CategoriesEditor';
import { DailySpecialEditor } from '../components/admin/DailySpecialEditor';
import { RestaurantInfoEditor } from '../components/admin/RestaurantInfoEditor';
import { AnalyticsPanel } from '../components/admin/AnalyticsPanel';
import { AllergensEditor } from '../components/admin/AllergensEditor';
import { ExtrasEditor } from '../components/admin/ExtrasEditor';
import { getAllergens, getExtras } from '../utils/menuExtras';

const resolveImgUrl = (url, api) => {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${api.replace(/\/api$/, '')}${url}`;
  return url;
};

const TABS = [
  { key: 'items',      labelIt: 'Piatti',           labelEn: 'Dishes' },
  { key: 'extras',     labelIt: 'Allergeni & Aggiunte', labelEn: 'Allergens & Extras' },
  { key: 'categories', labelIt: 'Foto categorie',   labelEn: 'Category photos' },
  { key: 'special',    labelIt: 'Piatto del giorno',labelEn: 'Daily special' },
  { key: 'info',       labelIt: 'Locale & Social',  labelEn: 'Place & Social' },
  { key: 'analytics',  labelIt: 'Analytics',        labelEn: 'Analytics' },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { isAdmin, authChecked, login, logout, menu, saveMenu, t, lang, API } = useApp();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [selectedSub, setSelectedSub] = useState('pinse-tradizionali');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('items');

  const draftApi = useMenuDraft(menu);
  const { draft } = draftApi;
  const translate = useTranslate(API);
  const batchTranslate = useBatchTranslate(API);
  const [translating, setTranslating] = useState(false);

  const resolveImg = (u) => resolveImgUrl(u, API);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await login(password);
      setPassword('');
    } catch (err) {
      setError('Password non corretta');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await saveMenu(draft);
      setMsg(t('saved'));
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('Errore: ' + e.message);
    }
    setSaving(false);
  };

  const handleTranslateAllEmpty = async () => {
    const jobs = draftApi.collectEmptyEnFields();
    if (jobs.length === 0) {
      setMsg(lang === 'it' ? 'Nessun campo EN vuoto da tradurre' : 'No empty EN fields');
      setTimeout(() => setMsg(''), 2500);
      return;
    }
    const confirmMsg = lang === 'it'
      ? `Tradurre ${jobs.length} campo/i EN vuoto/i? Richiederà qualche secondo.`
      : `Translate ${jobs.length} empty EN field(s)? It will take a few seconds.`;
    if (!window.confirm(confirmMsg)) return;
    setTranslating(true);
    setMsg(lang === 'it' ? `Traduzione in corso… (${jobs.length})` : `Translating… (${jobs.length})`);
    try {
      const { results, ok, total } = await batchTranslate(jobs);
      draftApi.applyBatchTranslations(results);
      const okMsg = lang === 'it'
        ? `${ok}/${total} tradotti. Ricorda di salvare!`
        : `${ok}/${total} translated. Remember to save!`;
      setMsg(okMsg);
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setMsg('Errore: ' + e.message);
    }
    setTranslating(false);
  };

  const uploadPhoto = async (file, onDone) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/admin/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (res.status === 401) {
        logout();
        alert('Sessione scaduta, effettua di nuovo il login.');
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload fallito');
      }
      const data = await res.json();
      onDone(data.url);
    } catch (e) {
      alert('Errore upload: ' + e.message);
    }
  };

  // Still checking session? Avoid flash of login form
  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center text-black/40">…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white border-[1.5px] border-black rounded-[20px] p-8 md:p-10 w-full max-w-md">
          <div className="flex items-center justify-center mb-6">
            <Logo height={44} />
          </div>
          <h1 className="font-display text-3xl text-center text-black mb-6">{t('admin')}</h1>
          <form onSubmit={submitLogin} className="space-y-4" data-testid="admin-login-form">
            <label className="block">
              <span className="text-xs font-bold text-black/60 uppercase tracking-wide">{t('password')}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="mt-1 w-full rounded-full border-[1.5px] border-black bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-black/30 font-semibold"
                autoFocus
              />
            </label>
            {error && <p className="text-red-700 text-sm font-bold" data-testid="admin-login-error">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" className="pill-btn primary flex-1" data-testid="admin-login-submit">{t('login')}</button>
              <button type="button" onClick={() => navigate('/')} className="pill-btn">{t('back')}</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (!draft) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="relative min-h-screen w-full pt-6 pb-24 px-4 md:px-8">
      <div className="relative max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="w-10 h-10 rounded-full border-[1.5px] border-black flex items-center justify-center hover:bg-black hover:text-white transition">
              <ArrowLeft size={16} />
            </button>
            <Logo height={38} />
            <h1 className="font-display text-2xl md:text-3xl text-black ml-2 hidden md:block">{t('manageMenu')}</h1>
          </div>
          <div className="flex items-center gap-2">
            {msg && <span className="font-bold text-black bg-white border-[1.5px] border-black px-3 py-1.5 rounded-full text-sm" data-testid="admin-save-msg">{msg}</span>}
            <button
              onClick={handleTranslateAllEmpty}
              disabled={translating || saving}
              className="pill-btn text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-translate-all-btn"
              title={lang === 'it' ? 'Traduci tutti i campi EN vuoti' : 'Translate all empty EN fields'}
            >
              <Languages size={14} /> {translating ? '...' : (lang === 'it' ? 'Traduci vuoti' : 'Translate empty')}
            </button>
            <button onClick={handleSave} disabled={saving} className="pill-btn primary" data-testid="admin-save-btn">
              <Save size={14} /> {saving ? '...' : t('save')}
            </button>
            <button onClick={logout} className="pill-btn" data-testid="admin-logout-btn">
              <LogOut size={14} /> {t('logout')}
            </button>
          </div>
        </div>

        {/* Mode tabs */}
        <div className="inline-flex items-center rounded-full border-[1.5px] border-black overflow-hidden text-sm font-bold mb-6 flex-wrap" data-testid="admin-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setMode(tab.key)}
              data-testid={`admin-tab-${tab.key}`}
              className={`px-5 h-10 transition ${mode === tab.key ? 'bg-black text-white' : 'bg-white text-black hover:bg-black/5'}`}
            >
              {lang === 'it' ? tab.labelIt : tab.labelEn}
            </button>
          ))}
        </div>

        {mode === 'extras' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <AllergensEditor allergens={getAllergens(draft)} onChange={(v) => draftApi.setTopLevel('allergens', v)} translate={translate} />
            <ExtrasEditor extras={getExtras(draft)} onChange={(v) => draftApi.setTopLevel('extras', v)} draft={draft} translate={translate} />
          </div>
        )}
        {mode === 'categories' && (
          <CategoriesEditor
            draft={draft}
            lang={lang}
            t={t}
            uploadPhoto={uploadPhoto}
            resolveImg={resolveImg}
            updateMainCategoryImage={draftApi.updateMainCategoryImage}
            updateSubCategoryImage={draftApi.updateSubCategoryImage}
          />
        )}
        {mode === 'special' && (
          <DailySpecialEditor
            special={draft.dailySpecial || {}}
            onMeta={draftApi.updateDailySpecialMeta}
            onItem={draftApi.updateDailySpecialItem}
            onAdd={draftApi.addDailySpecialItem}
            onRemove={draftApi.removeDailySpecialItem}
            uploadPhoto={uploadPhoto}
            resolveImg={resolveImg}
            lang={lang}
            translate={translate}
          />
        )}
        {mode === 'info' && (
          <RestaurantInfoEditor
            info={draft.restaurantInfo || {}}
            onChange={draftApi.updateRestaurantInfo}
            lang={lang}
          />
        )}
        {mode === 'analytics' && (
          <AnalyticsPanel API={API} lang={lang} />
        )}
        {mode === 'items' && (
          <ItemsEditor
            draft={draft}
            selectedSub={selectedSub}
            setSelectedSub={setSelectedSub}
            lang={lang}
            t={t}
            updateItem={draftApi.updateItem}
            addItem={draftApi.addItem}
            deleteItem={draftApi.deleteItem}
            reorderItems={draftApi.reorderItems}
            reorderSubs={draftApi.reorderSubs}
            uploadPhoto={uploadPhoto}
            resolveImg={resolveImg}
            translate={translate}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPage;
